/**
 * useLandingSeo — per-visit SEO for the public landing (a client-rendered SPA):
 * localised <title>/description, Open Graph + Twitter tags, canonical, and
 * JSON-LD (Organization, SoftwareApplication, FAQPage). Everything is restored
 * on unmount so the logged-in app is untouched. No ratings/offers are emitted —
 * only facts that exist in the product.
 */

import { useEffect } from 'react';

import { useT } from '@/i18n';
import { OG_IMAGE_PATH, SITE_ORIGIN } from '@/routes/landing/landing-media';
import { FAQ_KEYS } from './sections/Faq';

const LD_ID = 'landing-jsonld';

function upsertMeta(attr: 'name' | 'property', key: string, content: string, undo: Array<() => void>) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) {
    const prev = el.getAttribute('content');
    el.setAttribute('content', content);
    undo.push(() => (prev === null ? el!.removeAttribute('content') : el!.setAttribute('content', prev)));
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('content', content);
    document.head.appendChild(el);
    const created = el;
    undo.push(() => created.remove());
  }
}

export function useLandingSeo() {
  const { t, locale } = useT();

  useEffect(() => {
    const undo: Array<() => void> = [];
    const title = t('routes.landing.v2.seo.title');
    const description = t('routes.landing.v2.seo.description');
    const image = `${SITE_ORIGIN}${OG_IMAGE_PATH}`;

    const prevTitle = document.title;
    document.title = title;
    undo.push(() => {
      document.title = prevTitle;
    });

    upsertMeta('name', 'description', description, undo);
    upsertMeta('property', 'og:type', 'website', undo);
    upsertMeta('property', 'og:site_name', 'Karexia', undo);
    upsertMeta('property', 'og:title', title, undo);
    upsertMeta('property', 'og:description', description, undo);
    upsertMeta('property', 'og:url', `${SITE_ORIGIN}/`, undo);
    upsertMeta('property', 'og:image', image, undo);
    upsertMeta('property', 'og:image:width', '1200', undo);
    upsertMeta('property', 'og:image:height', '630', undo);
    upsertMeta('property', 'og:image:alt', t('routes.landing.v2.seo.ogAlt'), undo);
    upsertMeta('name', 'twitter:card', 'summary_large_image', undo);
    upsertMeta('name', 'twitter:title', title, undo);
    upsertMeta('name', 'twitter:description', description, undo);
    upsertMeta('name', 'twitter:image', image, undo);

    const graph = [
      {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: 'Karexia',
        url: `${SITE_ORIGIN}/`,
        logo: `${SITE_ORIGIN}/icons/icon-512.png`,
        email: 'info@karexia.com',
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${SITE_ORIGIN}/#software`,
        name: 'Karexia',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: `${SITE_ORIGIN}/`,
        description,
        image,
        inLanguage: ['fr', 'en', 'ar'],
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_ORIGIN}/#faq`,
        inLanguage: locale,
        mainEntity: FAQ_KEYS.map((n) => ({
          '@type': 'Question',
          name: t(`routes.landing.v2.faq.q${n}`),
          acceptedAnswer: { '@type': 'Answer', text: t(`routes.landing.v2.faq.a${n}`) },
        })),
      },
    ];
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = LD_ID;
    script.text = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    document.head.appendChild(script);
    undo.push(() => script.remove());

    return () => undo.reverse().forEach((fn) => fn());
  }, [t, locale]);
}
